import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  ElementNode,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  LexicalNode,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';

import {
  $createFontSizeNode,
  $isFontSizeNode,
} from '../FontSizePlugin/FontSizeNode';
import {
  $createHighlightNode,
  $findHighlightNode,
  HighlightColor,
} from '../HighlightPlugin';

/** The inline styling continued onto a new line when the user presses Enter. */
export interface ContinuationStyle {
  fontSize: number | null;
  color: HighlightColor | null;
}

function styleIsEmpty(style: ContinuationStyle): boolean {
  return style.fontSize === null && style.color === null;
}

function stylesAreEqual(a: ContinuationStyle, b: ContinuationStyle): boolean {
  return a.fontSize === b.fontSize && a.color === b.color;
}

/**
 * Walk up from `node` to the nearest block (non-inline) element. Returns the
 * paragraph / list item / heading that directly contains the caret. Inline
 * wrappers like FontSizeNode and HighlightNode are skipped.
 */
export function $getBlockElement(node: LexicalNode | null): ElementNode | null {
  let current = node;
  while (current !== null) {
    if ($isElementNode(current) && !current.isInline()) {
      return current;
    }
    current = current.getParent();
  }
  return null;
}

/**
 * Reads the inline styling (font size + highlight color) active at the given
 * node by inspecting its FontSizeNode / HighlightNode ancestors.
 */
export function $readContinuationStyle(node: LexicalNode): ContinuationStyle {
  // $findMatchingParent inspects ancestors starting from the parent, so a caret
  // sitting inside the FontSizeNode's text child still resolves the wrapper.
  const fontSizeNode = $findMatchingParent(node, $isFontSizeNode);
  const highlightNode = $findHighlightNode(node);
  return {
    fontSize: fontSizeNode ? fontSizeNode.getFontSize() : null,
    color: highlightNode ? highlightNode.getColor() : null,
  };
}

/**
 * Wrap a freshly-typed text node in the inline wrappers described by `style`.
 * FontSizeNode is the inner wrapper and HighlightNode the outer, matching the
 * nesting produced when both are applied via the toolbar (so markdown export
 * round-trips identically).
 */
export function $wrapNodeWithStyle(
  node: TextNode,
  style: ContinuationStyle
): void {
  let current: LexicalNode = node;
  if (style.fontSize !== null) {
    const fontSizeNode = $createFontSizeNode(style.fontSize);
    current.insertBefore(fontSizeNode);
    fontSizeNode.append(current);
    current = fontSizeNode;
  }
  if (style.color !== null) {
    const highlightNode = $createHighlightNode(style.color);
    current.insertBefore(highlightNode);
    highlightNode.append(current);
    current = highlightNode;
  }
}

/**
 * Keeps inline styling (font size, highlight, and — via Lexical's built-in
 * paragraph text-format carry-over — bold/italic/strikethrough) going when the
 * user presses Enter to start a new paragraph or list item.
 *
 * Font size and highlight are represented as inline wrapper nodes
 * (FontSizeNode / HighlightNode), which Lexical drops when splitting into a
 * fresh, empty block. An empty wrapper cannot be pre-seeded onto the new line
 * because Lexical garbage-collects empty inline elements. Instead we remember
 * the style at the caret when Enter is pressed and wrap the first text node the
 * user types on the new line.
 */
export function registerContinueStyle(editor: LexicalEditor): () => void {
  // The style to apply to the next text node typed on `blockKey`. Cleared
  // once applied, or when the caret leaves the pending block without typing.
  // `movedText` maps each text node that Enter moved to the new line to its
  // known text. Moved text keeps its own style, so the transform wraps only
  // characters typed before it.
  let pending: {
    style: ContinuationStyle;
    blockKey: string;
    movedText: Map<string, string>;
  } | null = null;

  return mergeRegister(
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        // Shift+Enter inserts a soft line break within the same block, so the
        // existing wrappers already apply — let the default handler run.
        if (event?.shiftKey) return false;

        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          pending = null;
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const style = $readContinuationStyle(anchorNode);

        if (styleIsEmpty(style)) {
          // The current line has no active style — either a genuinely unstyled
          // line, or a blank line left behind by a previous continuation the
          // user never typed on. Pressing Enter from a blank block should
          // start fresh, so drop any pending style and let the default handler
          // create an unstyled block.
          pending = null;
          return false;
        }

        // Perform the split ourselves (list-aware, since @lexical/list also
        // handles INSERT_PARAGRAPH_COMMAND) so we can key the pending style
        // to the newly created block.
        event?.preventDefault();
        const handled = editor.dispatchCommand(
          INSERT_PARAGRAPH_COMMAND,
          undefined
        );
        if (!handled) {
          pending = null;
          return false;
        }

        const newSelection = $getSelection();
        const newBlock = $isRangeSelection(newSelection)
          ? $getBlockElement(newSelection.anchor.getNode())
          : null;
        pending = newBlock
          ? {
              style,
              blockKey: newBlock.getKey(),
              movedText: new Map(
                newBlock
                  .getAllTextNodes()
                  .map((textNode) => [
                    textNode.getKey(),
                    textNode.getTextContent(),
                  ])
              ),
            }
          : null;
        return true;
      },
      COMMAND_PRIORITY_LOW
    ),
    editor.registerNodeTransform(TextNode, (node) => {
      if (pending === null) return;
      const block = $getBlockElement(node);
      if (block === null || block.getKey() !== pending.blockKey) return;
      const { style } = pending;

      let typedNode = node;
      const movedText = pending.movedText.get(node.getKey());
      if (movedText === undefined) {
        // Wrap the first bare text node typed on the fresh line. Subsequent
        // characters extend this same wrapper because FontSizeNode and
        // HighlightNode both return `canInsertTextAfter() === true`, so they
        // no longer break out into unstyled siblings. Skip nodes already
        // inside a wrapper (nothing to do).
        if ($isFontSizeNode(node.getParent())) return;
        if ($findHighlightNode(node.getParent())) return;
      } else {
        const text = node.getTextContent();
        if (text === movedText) return;
        if (!text.endsWith(movedText)) {
          // Another edit, such as Delete, changed the moved text. Track its
          // new text so that text typed before it later is still found.
          pending.movedText.set(node.getKey(), text);
          return;
        }
        // Text typed at the start of the new line goes into the moved text
        // node that follows the caret, inside that node's wrappers. If those
        // wrappers already have the continued style, the typed text is done.
        if (stylesAreEqual($readContinuationStyle(node), style)) {
          pending = null;
          return;
        }
        // Split off the typed prefix and move it out of the wrappers. The
        // prefix is the first descendant of each wrapper, so it goes directly
        // before the outermost wrapper.
        const [prefix] = node.splitText(text.length - movedText.length);
        if (!prefix) return;
        let outermost: LexicalNode = prefix;
        while (outermost.getParentOrThrow().getKey() !== block.getKey()) {
          outermost = outermost.getParentOrThrow();
        }
        if (outermost !== prefix) outermost.insertBefore(prefix);
        typedNode = prefix;
      }

      // Clear before wrapping: $wrapNodeWithStyle mutates the tree and would
      // otherwise re-trigger this transform on the same (now-wrapped) node.
      pending = null;
      $wrapNodeWithStyle(typedNode, style);
    }),
    // If the caret leaves the pending block before the user types (e.g. they
    // click elsewhere or arrow away), abandon the pending style.
    editor.registerUpdateListener(({ editorState }) => {
      const current = pending;
      if (current === null) return;
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          pending = null;
          return;
        }
        const block = $getBlockElement(selection.anchor.getNode());
        if (block === null || block.getKey() !== current.blockKey) {
          pending = null;
        }
      });
    })
  );
}

export function ContinueStylePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => registerContinueStyle(editor), [editor]);

  return null;
}
